import React from 'react';
import { ExternalLink, ChevronRight, Mail, Phone } from 'lucide-react';
import { IconTile } from '../hospital/shared/primitives';

/*
 * Rows for the grouped-list pages (Research Hub, BCM pages): a link row, a numbered row list,
 * and an intro card. Each takes the page's accent colour so the pages stay distinct.
 */

const rowClass = 'w-full flex items-center gap-3 px-4 py-3 text-left';

/** A row that opens a link (external, mail, tel), runs an action, or just sits there */
export const LinkRow = ({ icon, title, subtitle, href, onClick, muted = false, accent }) => {
  const isExternal = href && /^https?:/.test(href);
  const isMail = href && href.startsWith('mailto:');
  const isTel = href && href.startsWith('tel:');
  const Trail = isMail ? Mail : isTel ? Phone : isExternal ? ExternalLink : (href || onClick) ? ChevronRight : null;
  const inner = (
    <>
      {icon && <IconTile icon={icon} accent={accent} />}
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-[15px] ${muted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>{title}</div>
        {subtitle && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{subtitle}</div>}
      </div>
      {Trail && <Trail size={16} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />}
    </>
  );
  if (href) {
    return <a href={href} target={isExternal ? '_blank' : undefined} rel={isExternal ? 'noopener noreferrer' : undefined} className={`${rowClass} hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors`}>{inner}</a>;
  }
  if (onClick) {
    return <button type="button" onClick={onClick} className={`${rowClass} hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors`}>{inner}</button>;
  }
  return <div className={rowClass}>{inner}</div>;
};

/** Numbered rows: [title, text] pairs */
export const NumberedRows = ({ items, accent }) => (
  <ol>
    {items.map(([title, text], i) => (
      <li key={i} className="flex items-start gap-3 px-4 py-3">
        <span className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${accent}1f`, color: accent }}>{i + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>
          {text && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{text}</div>}
        </div>
      </li>
    ))}
  </ol>
);

/** One short paragraph with an icon, used at the top of a tab or a card */
export const IntroRow = ({ icon, title, children, accent }) => (
  <div className="px-4 py-3 flex items-start gap-3">
    <IconTile icon={icon} accent={accent} />
    <div className="flex-1 min-w-0">
      {title && <div className="font-medium text-[15px] text-gray-900 dark:text-white">{title}</div>}
      <p className={`text-sm text-gray-600 dark:text-gray-400 leading-snug ${title ? 'mt-1' : ''}`}>{children}</p>
    </div>
  </div>
);
